import {LoadSkillToolCall} from 'llm/chat-library/tool-details/ParsedProgressToolCall';

export interface ParsedSkillResponse {
  /** The main content (instructions or file body) */
  content: string;
  /** Available files listed in the response (instructions-only responses) */
  availableFiles: Array<{path: string; size?: string}>;
}

/**
 * Extract structured content from the LoadSkill XML response.
 *
 * The backend wraps skill content in pseudo-XML tags (see load_skill_tool.py).
 * We use simple regex extraction rather than DOMParser because the text nodes
 * may contain characters that break strict XML parsing (e.g. `<div>` in markdown).
 *
 * If you change anything here, you probably also need to update the tests in: tests/unit/llm/tools/load_skill_tool_test.py
 */
export function parseSkillResponse(rawResponse: string): ParsedSkillResponse | null {
  // Case 1: Instructions response — <instructions>...</instructions>
  const instructionsMatch = rawResponse.match(/<instructions>\n?([\s\S]*?)\n?<\/instructions>/);
  if (instructionsMatch) {
    const content = stripFrontmatter(instructionsMatch[1].trim());
    const availableFiles: ParsedSkillResponse['availableFiles'] = [];
    for (const fileMatch of rawResponse.matchAll(/<file\s+(.*?)\s*\/>/g)) {
      const attrs = fileMatch[1];
      const path = attrs.match(/path="([^"]*)"/)?.[1];
      const size = attrs.match(/size="([^"]*)"/)?.[1];
      if (path) {
        availableFiles.push({path, size});
      }
    }
    return {content, availableFiles};
  }

  // Case 2: File content response — <file path="...">...</file>
  const fileMatch = rawResponse.match(/<file\s+path="[^"]*">\n?([\s\S]*?)\n?<\/file>/);
  if (fileMatch) {
    return {content: stripFrontmatter(extractTextContent(fileMatch[1].trim())), availableFiles: []};
  }

  return null;
}

/**
 * Strip YAML frontmatter (delimited by `---`) from the beginning of a string.
 *
 * Skill files (especially SKILL.md) may begin with frontmatter containing
 * metadata like `name` and `description` that duplicates what the backend
 * already exposes as structured fields.  We strip it so the popup shows
 * only the actual skill content.
 */
function stripFrontmatter(text: string): string {
  const match = text.match(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/);
  if (match) {
    return text.slice(match[0].length).trimStart();
  }
  return text;
}

/**
 * Extract the deepest text content from a file body.
 *
 * Non-plaintext files go through document extraction on the backend
 * (format_uploaded_file_for_llm), which wraps content in:
 *   <document><source>...</source><document-content><text-content>BODY</text-content>...</document-content></document>
 * Strip those wrappers so the popup shows only the actual file content.
 */
function extractTextContent(body: string): string {
  const textContentMatch = body.match(/<text-content>\n?([\s\S]*?)\n?<\/text-content>/);
  if (textContentMatch) {
    return textContentMatch[1].trim();
  }
  return body;
}

export function formatSkillResponseAsMarkdown(parsed: ParsedSkillResponse): string {
  const sections: Array<string> = [parsed.content];
  if (parsed.availableFiles.length > 0) {
    const fileList = parsed.availableFiles
      .map((f) => (f.size ? `- \`${f.path}\` (${f.size})` : `- \`${f.path}\``))
      .join('\n');
    sections.push(`**Available files:**\n${fileList}`);
  }
  return sections.join('\n\n');
}

export function formatLoadSkillInput(toolCall: LoadSkillToolCall): string {
  const parts: Array<string> = [];
  if (toolCall.skillName) {
    parts.push(`Skill: ${toolCall.skillName}`);
  }
  if (toolCall.filePath) {
    parts.push(`File: ${toolCall.filePath}`);
  } else {
    parts.push(`File: SKILL.md`);
  }
  return parts.length > 0 ? parts.join('\n') : 'Loading skill instructions';
}
